import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  User, CreditCard, Bell, Shield, Crown, Users, Plus, 
  Copy, Check, AlertTriangle, Smartphone, Key, Trash2, 
  UserPlus, Settings2, LogOut, Mail, MoreVertical
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 2FA Setup Component
const TwoFactorSetup = ({ user, onStatusChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [isDisableOpen, setIsDisableOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/2fa/status`, { withCredentials: true });
      setStatus(response.data);
      if (onStatusChange) onStatusChange(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const initiateSetup = async () => {
    try {
      const response = await axios.post(`${API}/2fa/setup`, {}, { withCredentials: true });
      setSetupData(response.data);
      setIsSetupOpen(true);
    } catch (error) {
      toast.error('Failed to initialize 2FA setup');
    }
  };

  const confirmSetup = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      const response = await axios.post(`${API}/2fa/confirm`, { token: verificationCode }, { withCredentials: true });
      if (response.data.success) {
        toast.success('Two-factor authentication enabled!');
        setShowBackupCodes(true);
        fetchStatus();
      } else {
        toast.error(response.data.error || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to confirm 2FA');
    }
  };

  const disable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      const response = await axios.post(`${API}/2fa/disable`, { token: disableCode }, { withCredentials: true });
      if (response.data.success) {
        toast.success('Two-factor authentication disabled');
        setIsDisableOpen(false);
        setDisableCode('');
        fetchStatus();
      } else {
        toast.error(response.data.error || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyAllCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      toast.success('All backup codes copied!');
    }
  };

  if (loading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg"></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border border-border rounded-lg">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${status?.enabled ? 'bg-green-500/10' : 'bg-muted'}`}>
            <Smartphone className={`w-5 h-5 ${status?.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <div className="font-medium flex items-center gap-2">
              Two-Factor Authentication
              {status?.enabled && <Badge className="bg-green-500 text-white">Enabled</Badge>}
            </div>
            <div className="text-sm text-muted-foreground">
              {status?.enabled 
                ? `Protected since ${new Date(status.enabled_at).toLocaleDateString()}`
                : 'Add an extra layer of security to your account'
              }
            </div>
          </div>
        </div>
        {status?.enabled ? (
          <Dialog open={isDisableOpen} onOpenChange={setIsDisableOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm" data-testid="disable-2fa-btn">
                Disable
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogDescription>
                  Enter a code from your authenticator app to disable 2FA.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 flex justify-center">
                <InputOTP maxLength={6} value={disableCode} onChange={setDisableCode} data-testid="disable-2fa-otp">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button variant="destructive" onClick={disable2FA} data-testid="confirm-disable-2fa">
                  Disable 2FA
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Button onClick={initiateSetup} data-testid="enable-2fa-btn">
            Enable 2FA
          </Button>
        )}
      </div>

      {/* Backup Codes Warning */}
      {status?.enabled && status?.backup_codes_remaining <= 3 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Only {status.backup_codes_remaining} backup codes remaining. Consider regenerating them.
          </AlertDescription>
        </Alert>
      )}

      {/* 2FA Setup Dialog */}
      <Dialog open={isSetupOpen && !showBackupCodes} onOpenChange={setIsSetupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Or enter this code manually:</p>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{setupData.secret}</code>
              </div>
              <div className="space-y-2">
                <Label>Enter verification code</Label>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} data-testid="setup-2fa-otp">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={confirmSetup} disabled={verificationCode.length !== 6} data-testid="confirm-2fa-setup">
              Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={(open) => { if (!open) { setShowBackupCodes(false); setSetupData(null); setVerificationCode(''); setIsSetupOpen(false); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Save Your Backup Codes
            </DialogTitle>
            <DialogDescription>
              Store these codes in a safe place. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          {setupData?.backup_codes && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {setupData.backup_codes.map((code, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded font-mono text-sm">
                    <span>{code}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(code, index)}>
                      {copiedCode === index ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={copyAllCodes}>
                <Copy className="w-4 h-4 mr-2" /> Copy All Codes
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setShowBackupCodes(false); setSetupData(null); setVerificationCode(''); setIsSetupOpen(false); }} data-testid="backup-codes-done">
              I've Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Workspace Management Component
const WorkspaceManagement = ({ user }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDesc, setNewWorkspaceDesc] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  const fetchWorkspaces = async () => {
    try {
      const response = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(response.data.workspaces || []);
      setActiveWorkspaceId(response.data.active_workspace_id);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }
    try {
      await axios.post(`${API}/workspaces`, {
        name: newWorkspaceName,
        description: newWorkspaceDesc
      }, { withCredentials: true });
      toast.success('Workspace created!');
      setCreateDialogOpen(false);
      setNewWorkspaceName('');
      setNewWorkspaceDesc('');
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    }
  };

  const switchWorkspace = async (workspaceId) => {
    try {
      await axios.post(`${API}/workspaces/${workspaceId}/switch`, {}, { withCredentials: true });
      setActiveWorkspaceId(workspaceId);
      toast.success('Workspace switched!');
    } catch (error) {
      toast.error('Failed to switch workspace');
    }
  };

  const openWorkspaceDetails = async (workspace) => {
    setSelectedWorkspace(workspace);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/workspaces/${workspace.workspace_id}/members`, { withCredentials: true }),
        axios.get(`${API}/workspaces/${workspace.workspace_id}/invites`, { withCredentials: true })
      ]);
      setMembers(membersRes.data.members || []);
      setInvites(invitesRes.data.invites || []);
      setInviteDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load workspace details');
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }
    try {
      await axios.post(`${API}/workspaces/${selectedWorkspace.workspace_id}/invite`, {
        email: inviteEmail,
        role: inviteRole
      }, { withCredentials: true });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      // Refresh invites
      const invitesRes = await axios.get(`${API}/workspaces/${selectedWorkspace.workspace_id}/invites`, { withCredentials: true });
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const updateMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/workspaces/${selectedWorkspace.workspace_id}/members/${memberId}/role`, 
        { role: newRole }, 
        { withCredentials: true }
      );
      toast.success('Role updated!');
      // Refresh members
      const membersRes = await axios.get(`${API}/workspaces/${selectedWorkspace.workspace_id}/members`, { withCredentials: true });
      setMembers(membersRes.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/workspaces/${selectedWorkspace.workspace_id}/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      // Refresh members
      const membersRes = await axios.get(`${API}/workspaces/${selectedWorkspace.workspace_id}/members`, { withCredentials: true });
      setMembers(membersRes.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const revokeInvite = async (email) => {
    try {
      await axios.delete(`${API}/workspaces/${selectedWorkspace.workspace_id}/invites/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Invite revoked');
      // Refresh invites
      const invitesRes = await axios.get(`${API}/workspaces/${selectedWorkspace.workspace_id}/invites`, { withCredentials: true });
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const deleteWorkspace = async (workspaceId) => {
    if (!confirm('Are you sure? This will delete the workspace and remove all members.')) return;
    try {
      await axios.delete(`${API}/workspaces/${workspaceId}`, { withCredentials: true });
      toast.success('Workspace deleted');
      setInviteDialogOpen(false);
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workspace');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-500';
      case 'admin': return 'bg-blue-500';
      case 'editor': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg"></div>;
  }

  return (
    <div className="space-y-6">
      {/* Workspaces List */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Your Workspaces</h3>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-workspace-btn">
              <Plus className="w-4 h-4 mr-2" /> New Workspace
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Workspace</DialogTitle>
              <DialogDescription>
                Create a new workspace to collaborate with your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Workspace Name</Label>
                <Input 
                  placeholder="My Agency" 
                  value={newWorkspaceName} 
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  data-testid="workspace-name-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input 
                  placeholder="Team workspace for..." 
                  value={newWorkspaceDesc} 
                  onChange={(e) => setNewWorkspaceDesc(e.target.value)}
                  data-testid="workspace-desc-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={createWorkspace} data-testid="confirm-create-workspace">
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {workspaces.length === 0 ? (
        <div className="text-center py-12 bg-muted/50 rounded-lg">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h4 className="font-medium mb-2">No Workspaces Yet</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Create a workspace to start collaborating with your team
          </p>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Your First Workspace
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {workspaces.map((workspace) => (
            <div 
              key={workspace.workspace_id} 
              className={`p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${activeWorkspaceId === workspace.workspace_id ? 'border-primary bg-primary/5' : 'border-border'}`}
              onClick={() => openWorkspaceDetails(workspace)}
              data-testid={`workspace-${workspace.workspace_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {workspace.name}
                      {activeWorkspaceId === workspace.workspace_id && (
                        <Badge variant="outline" className="text-xs">Active</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {workspace.member_count || 1} member{(workspace.member_count || 1) !== 1 ? 's' : ''} · 
                      <Badge className={`${getRoleBadgeColor(workspace.role)} text-white ml-2 text-xs`}>
                        {workspace.role}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {activeWorkspaceId !== workspace.workspace_id && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); switchWorkspace(workspace.workspace_id); }}
                    >
                      Switch
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openWorkspaceDetails(workspace); }}>
                    <Settings2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Workspace Details Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedWorkspace && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {selectedWorkspace.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedWorkspace.description || 'Manage workspace members and settings'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Invite Section */}
                {(selectedWorkspace.role === 'owner' || selectedWorkspace.role === 'admin') && (
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4" /> Invite Team Member
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="email@example.com" 
                        value={inviteEmail} 
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="flex-1"
                        data-testid="invite-email-input"
                      />
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={sendInvite} data-testid="send-invite-btn">
                        <Mail className="w-4 h-4 mr-2" /> Invite
                      </Button>
                    </div>
                  </div>
                )}

                {/* Members List */}
                <div className="space-y-3">
                  <Label>Members ({members.length})</Label>
                  <div className="space-y-2">
                    {members.map((member) => (
                      <div key={member.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.picture} />
                            <AvatarFallback>{member.name?.charAt(0) || member.email?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-sm">{member.name || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${getRoleBadgeColor(member.role)} text-white text-xs`}>
                            {member.role}
                          </Badge>
                          {member.role !== 'owner' && (selectedWorkspace.role === 'owner' || selectedWorkspace.role === 'admin') && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => updateMemberRole(member.user_id, 'admin')}>
                                  Make Admin
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => updateMemberRole(member.user_id, 'editor')}>
                                  Make Editor
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => updateMemberRole(member.user_id, 'viewer')}>
                                  Make Viewer
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-destructive" 
                                  onClick={() => removeMember(member.user_id)}
                                >
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pending Invites */}
                {invites.length > 0 && (
                  <div className="space-y-3">
                    <Label>Pending Invites ({invites.length})</Label>
                    <div className="space-y-2">
                      {invites.map((invite, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-dashed">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{invite.email?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-sm">{invite.email}</div>
                              <div className="text-xs text-muted-foreground">Pending · {invite.role}</div>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => revokeInvite(invite.email)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Danger Zone */}
                {selectedWorkspace.role === 'owner' && (
                  <div className="pt-4 border-t">
                    <Label className="text-destructive">Danger Zone</Label>
                    <div className="mt-2 p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                      <p className="text-sm text-muted-foreground mb-3">
                        Deleting this workspace will remove all members and data permanently.
                      </p>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={() => deleteWorkspace(selectedWorkspace.workspace_id)}
                        data-testid="delete-workspace-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Workspace
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Settings Component
export const Settings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        toast.error('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'enterprise': return 'bg-purple-500';
      case 'pro': return 'bg-blue-500';
      case 'basic': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your account, team, and security</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-6">Profile Information</h3>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-primary" />
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-lg">{user?.name}</div>
                    <div className="text-sm text-muted-foreground">{user?.email}</div>
                  </div>
                </div>
                <div>
                  <Label>User ID</Label>
                  <div className="mt-1 p-3 bg-muted rounded-md text-sm font-mono">{user?.user_id}</div>
                </div>
                <div>
                  <Label>Member Since</Label>
                  <div className="mt-1 text-sm">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <Card className="p-6">
              <WorkspaceManagement user={user} />
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">Current Plan</h3>
                <Badge className={`${getTierColor(user?.subscription_tier)} text-white capitalize`}>
                  {user?.subscription_tier}
                </Badge>
              </div>

              {user?.subscription_tier === 'free' ? (
                <div className="space-y-6">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-bold mb-2">Free Plan Features</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Connect up to 2 platforms</li>
                      <li>• Basic dashboard analytics</li>
                      <li>• Simple audit outcomes</li>
                      <li>• Manual data upload</li>
                    </ul>
                  </div>
                  <div className="p-6 bg-primary text-primary-foreground rounded-lg">
                    <div className="flex items-start space-x-4">
                      <Crown className="w-8 h-8 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-lg font-bold mb-2">Upgrade to Pro</h4>
                        <p className="mb-4 opacity-90">
                          Unlock unlimited platforms, advanced analytics, comprehensive audits, and more
                        </p>
                        <Button data-testid="upgrade-to-pro-button" className="bg-primary-foreground text-primary hover:opacity-90">
                          Upgrade Now - $149/month
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-bold mb-2 capitalize">{user?.subscription_tier} Plan Features</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Unlimited platform connections</li>
                      <li>• Advanced analytics & reports</li>
                      <li>• Comprehensive audits</li>
                      <li>• Campaign planning tools</li>
                      <li>• Gap analysis</li>
                      <li>• Priority support</li>
                    </ul>
                  </div>
                  <Button variant="outline" data-testid="manage-subscription-button">
                    Manage Subscription
                  </Button>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-6">Notification Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <div className="font-medium">Campaign Performance Alerts</div>
                    <div className="text-sm text-muted-foreground">Get notified about significant performance changes</div>
                  </div>
                  <Button variant="outline" size="sm">Enable</Button>
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <div className="font-medium">Budget Alerts</div>
                    <div className="text-sm text-muted-foreground">Notifications when campaigns reach 80% of budget</div>
                  </div>
                  <Button variant="outline" size="sm">Enable</Button>
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <div className="font-medium">Weekly Reports</div>
                    <div className="text-sm text-muted-foreground">Receive weekly performance summaries</div>
                  </div>
                  <Button variant="outline" size="sm">Enable</Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-6">Security Settings</h3>
              <div className="space-y-6">
                {/* Authentication Status */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">Authentication</div>
                    <Badge className="bg-green-500 text-white">Active</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    You're signed in with Google OAuth
                  </div>
                </div>

                {/* 2FA Section */}
                <TwoFactorSetup user={user} />

                {/* Session Management */}
                <div className="p-4 border border-border rounded-lg">
                  <div className="font-medium mb-2">Session Management</div>
                  <div className="text-sm text-muted-foreground mb-3">
                    Your session is active and secure
                  </div>
                  <Button variant="destructive" size="sm" data-testid="end-session-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    End All Sessions
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
